import React, { useEffect, useRef, useState } from 'react';
import styles from './HowItWorksSection.module.css';
import { Trans, useTranslation } from 'react-i18next';

type Step = {
  title: string;
  description: string;
};

interface Props {
  steps: Step[];
  progress?: number;
  label?: string;
  title?: string;
  subtitle?: string;
  style?: React.CSSProperties;
}

const HowItWorks: React.FC<Props> = ({
  steps,
  style,
}) => {
  const { t } = useTranslation();
  const stepRefs = useRef<HTMLDivElement[]>([]);
  const [lines, setLines] = useState<{ left: number; width: number }[]>([]);

  useEffect(() => {
    const updateLines = () => {
      const container = stepRefs.current[0]?.closest(`.${styles.stepsWithLines}`) as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const gap = 8;

      const newLines: { left: number; width: number }[] = [];

      for (let i = 0; i < stepRefs.current.length - 1; i++) {
        const from = stepRefs.current[i]?.querySelector(`.${styles.step__number}`) as HTMLElement;
        const to = stepRefs.current[i + 1]?.querySelector(`.${styles.step__number}`) as HTMLElement;

        if (!from || !to) continue;

        const fromRect = from.getBoundingClientRect();
        const toRect = to.getBoundingClientRect();

        const fromRight = fromRect.right - containerRect.left + gap;
        const toLeft = toRect.left - containerRect.left - gap;

        newLines.push({
          left: fromRight,
          width: toLeft - fromRight,
        });
      }

      setLines(newLines);
    };

    updateLines();
    window.addEventListener('resize', updateLines);
    return () => window.removeEventListener('resize', updateLines);
  }, [steps]);

  return (
    <section className={styles.howitworks} style={style}>
      <div className={styles.howitworks__label}>{t('howItWorks.label')}</div>
      <h2 className={styles.howitworks__title}>{t('howItWorks.title')}</h2>
      <p className={styles.howitworks__subtitle}>{t('howItWorks.subtitle')}</p>

      <div className={styles.stepsWithLines}>
        {steps.map((step, i) => (
          <div
            className={styles.stepWrapper}
            key={i}
            ref={(el) => (stepRefs.current[i] = el!)}
          >
          <div className={styles.step__numberWrapper}>
            <div className={styles.step__number}>{i + 1}</div>
          </div>
            <h3 className={styles.step__title}>{t(step.title)}</h3>
            <p className={styles.step__description}> 
              <Trans i18nKey={step.description} components={{ br: <br />, underline: <span className={styles.underline} /> }} /></p>
          </div>
        ))}
        {lines.map((line, index) => (
          <div
            key={index}
            className={styles.line}
            style={{ left: `${line.left}px`, width: `${line.width}px` }}
          />
        ))}
      </div>
    </section>
  );
};

export default HowItWorks;